import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalyticsStore from '../stores/useAnalyticsStore';
import LineChart from '../components/LineChart';
import BarChart from '../components/BarChart';
import exportToPDF from '../utils/exportToPDF';
import useAuthStore from '../stores/useAuthStore';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';

const InstructorAnalyticsPage = () => {
    const { user } = useAuthStore();
    const toast = useToast();
    const navigate = useNavigate();
    const {
        instructorOverview,
        coursePerformance,
        engagementData,
        studentDemographics,
        geographicDistribution,
        cohortAnalysis,
        fetchInstructorOverview,
        fetchCoursePerformance,
        fetchEngagementData,
        fetchStudentDemographics,
        fetchGeographicDistribution,
        fetchCohortAnalysis,
        isLoading,
        error
    } = useAnalyticsStore();

    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
        endDate: new Date()
    });

    useEffect(() => {
        fetchInstructorOverview();
        fetchCoursePerformance();
        fetchStudentDemographics();
        fetchGeographicDistribution();
        fetchCohortAnalysis();
    }, []);

    useEffect(() => {
        fetchEngagementData(dateRange.startDate, dateRange.endDate);
    }, [dateRange]);

    const handleDateRangeChange = (range) => {
        setDateRange(range);
    };

    const exportReport = (tab) => {
        if (tab === 'demographics' && studentDemographics) {
            const columns = ['Demographic', 'Count'];
            const rows = [
                ...Object.entries(studentDemographics.ageGroups || {}).map(([key, value]) => [`Age: ${key}`, value.toString()]),
                ...Object.entries(studentDemographics.genderDistribution || {}).map(([key, value]) => [`Gender: ${key}`, value.toString()]),
                ...Object.entries(studentDemographics.educationDistribution || {}).map(([key, value]) => [`Education: ${key}`, value.toString()])
            ];
            exportToPDF(studentDemographics, 'Student_Demographics_Report', columns, rows);
        } else if (tab === 'engagement' && engagementData) {
            const allDates = [
                ...new Set([
                    ...(engagementData.revenue?.map(r => r.date) || []),
                    ...(engagementData.videoCompletion?.map(v => v.date) || []),
                    ...(engagementData.forum?.map(f => f.date) || [])
                ])
            ].sort();

            const columns = ['Date', 'Revenue (NGN)', 'Video Completions', 'Forum Threads', 'Forum Replies'];
            const rows = allDates.map(date => {
                const rev = engagementData.revenue?.find(r => r.date === date)?.amount || 0;
                const comp = engagementData.videoCompletion?.find(v => v.date === date)?.rate || 0;
                const forum = engagementData.forum?.find(f => f.date === date);

                return [
                    date,
                    rev.toLocaleString(),
                    comp.toString(),
                    (forum?.threads || 0).toString(),
                    (forum?.replies || 0).toString()
                ];
            });

            exportToPDF(null, 'Engagement_Report', columns, rows);
        } else if (tab === 'geography' && geographicDistribution) {
            const columns = ['Location', 'Count'];
            const rows = [
                ...Object.entries(geographicDistribution.countries || {}).map(([key, value]) => [`Country: ${key}`, value.toString()]),
                ...Object.entries(geographicDistribution.states || {}).map(([key, value]) => [`State: ${key}`, value.toString()]),
                ...Object.entries(geographicDistribution.cities || {}).map(([key, value]) => [`City: ${key}`, value.toString()])
            ];
            exportToPDF(geographicDistribution, 'Geographic_Distribution_Report', columns, rows);
        } else if (tab === 'cohorts' && cohortAnalysis) {
            const columns = ['Cohort', 'Month', 'Retention Rate'];
            const rows = cohortAnalysis.flatMap(cohort =>
                cohort.retentionData.map(data => [
                    cohort.cohort,
                    `Month ${data.month}`,
                    `${data.retentionRate.toFixed(2)}%`
                ])
            );
            exportToPDF(cohortAnalysis, 'Cohort_Analysis_Report', columns, rows);
        } else {
            const overviewData = [
                ['Metric', 'Value'],
                ['Total Revenue', instructorOverview?.totalRevenue?.toLocaleString()],
                ['Wallet Balance', instructorOverview?.walletBalance?.toLocaleString()],
                ['Total Earnings', instructorOverview?.totalEarnings?.toLocaleString()],
                ['Total Payout', instructorOverview?.totalWithdrawn?.toLocaleString()],
                ['Total Enrolled', instructorOverview?.totalStudents],
                ['Total Courses', instructorOverview?.totalCourses],
                ['Total Approved', instructorOverview?.totalApprovedCourses]
            ];
            exportToPDF(instructorOverview, 'Instructor_Analytics_Report', ['Metric', 'Value'], overviewData);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading analytics data</h2>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
                    <button
                        onClick={() => {
                            fetchInstructorOverview();
                            fetchCoursePerformance();
                            fetchEngagementData(dateRange.startDate, dateRange.endDate);
                        }}
                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <DashboardLayout userRole={user.role}>
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Instructor Analytics</h1>
                            <p className="text-neutral-600 dark:text-neutral-400">
                                Track your course performance, revenue, and student engagement
                            </p>
                        </div>
                        <button
                            onClick={() => exportReport(activeTab)}
                            className="px-4 py-2 bg-success-500 hover:bg-success-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500"
                        >
                            Export Report
                        </button>
                    </div>

                    {/* Date Range Picker */}
                    <div className="mb-8 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Select Date Range</h3>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-4 sm:space-y-0">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Start Date</label>
                                <input
                                    type="date"
                                    value={dateRange.startDate.toISOString().split('T')[0]}
                                    onChange={(e) => handleDateRangeChange({
                                        ...dateRange,
                                        startDate: new Date(e.target.value)
                                    })}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">End Date</label>
                                <input
                                    type="date"
                                    value={dateRange.endDate.toISOString().split('T')[0]}
                                    onChange={(e) => handleDateRangeChange({
                                        ...dateRange,
                                        endDate: new Date(e.target.value)
                                    })}
                                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        const now = new Date();
                                        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                                        setDateRange({
                                            startDate: thirtyDaysAgo,
                                            endDate: now
                                        });
                                    }}
                                    className="px-4 mt-6 py-2 bg-neutral-200 hover:bg-neutral-300 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-700 dark:text-neutral-300 text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                                >
                                    Reset to 30 Days
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="mb-8 border-b border-neutral-200 dark:border-neutral-700">
                        <nav className="-mb-px flex space-x-8">
                            {['overview', 'courses', 'engagement', 'demographics', 'geography', 'cohorts'].map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                                        }`}
                                >
                                    {tab.replace('-', ' ')}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Overview Tab */}
                    {activeTab === 'overview' && (
                        <div className="space-y-8">
                            {/* KPI Stats */}
                            {instructorOverview && (
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">🎓</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Courses</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    {instructorOverview.totalCourses}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">👥</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Enrolled</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    {instructorOverview.totalStudents}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">📈</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Revenue</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    ₦{instructorOverview?.totalRevenue?.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">💰</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Wallet Balance</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    ₦{instructorOverview?.walletBalance?.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">💵</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Earnings</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    ₦{instructorOverview?.totalEarnings?.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">💸</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Payout</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    ₦{instructorOverview?.totalWithdrawn?.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">⭐</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Course Ratings</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    {instructorOverview.avgCourseRating}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <div className="flex items-center">
                                            <div className="w-12 h-12 bg-info-100 dark:bg-info-900/30 rounded-full flex items-center justify-center mr-4">
                                                <span className="text-2xl">✅</span>
                                            </div>
                                            <div>
                                                <p className="text-sm text-neutral-600 dark:text-neutral-400">Approved Courses</p>
                                                <p className="text-2xl font-bold text-neutral-900 dark:text-white">
                                                    {instructorOverview.totalApprovedCourses}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Revenue Over Time */}
                            {engagementData?.revenue && (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Revenue Over Time</h2>
                                    <LineChart
                                        data={{
                                            labels: engagementData.revenue.map(item => item.date),
                                            datasets: [
                                                {
                                                    label: 'Daily Revenue',
                                                    data: engagementData.revenue.map(item => item.amount),
                                                    borderColor: '#10b981',
                                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                    fill: true
                                                }
                                            ]
                                        }}
                                        options={{
                                            scales: {
                                                y: {
                                                    ticks: {
                                                        callback: function (value) {
                                                            return `₦${value?.toLocaleString()}`;
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        height={300}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'courses' && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">Course Performance</h2>

                            </div>
                            {coursePerformance && coursePerformance?.length > 0 ? (
                                <div className="space-y-6">
                                    {/* Bar Chart: Courses by Enrollments */}
                                    <div className="mb-8">
                                        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Courses by Enrollments</h3>
                                        <BarChart
                                            data={{
                                                labels: coursePerformance.map(course => course?.title),
                                                datasets: [
                                                    {
                                                        label: 'Enrollments',
                                                        data: coursePerformance.map(course => course?.totalEnrollments),
                                                        backgroundColor: '#3b82f6'
                                                    }
                                                ]
                                            }}
                                            options={{
                                                plugins: {
                                                    title: {
                                                        display: false
                                                    }
                                                }
                                            }}
                                            height={400}
                                        />
                                    </div>

                                    {/* Table: Detailed Course Metrics */}
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                                            <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                                                <tr>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Course</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Enrollments</th>
                                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Revenue</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                                                {coursePerformance?.map((course) => (
                                                    <tr key={course?._id}>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm font-medium text-neutral-900 dark:text-white">{course?.title}</div>
                                                            <div className="text-sm text-neutral-500 dark:text-neutral-400">{course?.category?.name}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-neutral-900 dark:text-white">{course?.totalEnrollments}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-neutral-900 dark:text-white">₦{course?.revenue?.toLocaleString()}</div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-6xl mb-4">📊</div>
                                    <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses created yet</h3>
                                    <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                        Create your first course to start tracking performance
                                    </p>
                                    <button
                                        onClick={() => navigate('/instructor/course/builder')}
                                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        Create Course
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'engagement' && (
                        <div className="space-y-8">
                            {!engagementData ? (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <div className="text-center py-12">
                                        <div className="text-6xl mb-4">📈</div>
                                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No engagement data yet</h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                            Students need to enroll and interact with your courses to generate engagement metrics.
                                        </p>
                                        <button
                                            onClick={() => navigate('/instructor/create-course')}
                                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            Create Course
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Revenue Over Time */}
                                    {engagementData.revenue && engagementData.revenue.length > 0 && (
                                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Daily Revenue</h2>
                                            <LineChart
                                                data={{
                                                    labels: engagementData.revenue.map(item => item.date),
                                                    datasets: [
                                                        {
                                                            label: 'Revenue (NGN)',
                                                            data: engagementData.revenue.map(item => item.amount),
                                                            borderColor: '#10b981',
                                                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                            fill: true,
                                                            tension: 0.4
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    scales: {
                                                        y: {
                                                            beginAtZero: true,
                                                            ticks: {
                                                                callback: function (value) {
                                                                    return `₦${value.toLocaleString()}`;
                                                                }
                                                            }
                                                        }
                                                    },
                                                    plugins: {
                                                        legend: { display: true }
                                                    }
                                                }}
                                                height={300}
                                            />
                                        </div>
                                    )}

                                    {/* Video Completion & Retention */}
                                    {(engagementData.videoCompletion?.length > 0 || engagementData.retention?.length > 0) && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {engagementData.videoCompletion && engagementData.videoCompletion.length > 0 && (
                                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Daily Video Completions</h2>
                                                    <LineChart
                                                        data={{
                                                            labels: engagementData.videoCompletion.map(item => item.date),
                                                            datasets: [
                                                                {
                                                                    label: 'Completed Lessons',
                                                                    data: engagementData.videoCompletion.map(item => item.rate),
                                                                    borderColor: '#3b82f6',
                                                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                                    fill: true,
                                                                    tension: 0.4
                                                                }
                                                            ]
                                                        }}
                                                        options={{
                                                            scales: {
                                                                y: { beginAtZero: true }
                                                            }
                                                        }}
                                                        height={250}
                                                    />
                                                </div>
                                            )}

                                            {engagementData.retention && engagementData.retention.length > 0 && (
                                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Daily Course Completions</h2>
                                                    <LineChart
                                                        data={{
                                                            labels: engagementData.retention.map(item => item.date),
                                                            datasets: [
                                                                {
                                                                    label: 'Completed Courses',
                                                                    data: engagementData.retention.map(item => item.rate),
                                                                    borderColor: '#f59e0b',
                                                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                                                    fill: true,
                                                                    tension: 0.4
                                                                }
                                                            ]
                                                        }}
                                                        options={{
                                                            scales: {
                                                                y: { beginAtZero: true }
                                                            }
                                                        }}
                                                        height={250}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Forum Activity */}
                                    {engagementData.forum && engagementData.forum.length > 0 && (
                                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Forum Activity</h2>
                                            <BarChart
                                                data={{
                                                    labels: engagementData.forum.map(item => item.date),
                                                    datasets: [
                                                        {
                                                            label: 'New Threads',
                                                            data: engagementData.forum.map(item => item.threads),
                                                            backgroundColor: '#8b5cf6'
                                                        },
                                                        {
                                                            label: 'Replies',
                                                            data: engagementData.forum.map(item => item.replies),
                                                            backgroundColor: '#ec4899'
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    responsive: true,
                                                    scales: {
                                                        x: { stacked: true },
                                                        y: { stacked: true, beginAtZero: true }
                                                    },
                                                    plugins: {
                                                        legend: { position: 'top' }
                                                    }
                                                }}
                                                height={300}
                                            />
                                        </div>
                                    )}

                                    {/* Monthly Cohorts */}
                                    {engagementData.cohorts && engagementData.cohorts.length > 0 && (
                                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Monthly Student Cohorts</h2>
                                            <BarChart
                                                data={{
                                                    labels: engagementData.cohorts.map(item => item.month),
                                                    datasets: [
                                                        {
                                                            label: 'New Enrollments',
                                                            data: engagementData.cohorts.map(item => item.activeStudents),
                                                            backgroundColor: '#10b981'
                                                        }
                                                    ]
                                                }}
                                                options={{
                                                    scales: {
                                                        y: { beginAtZero: true }
                                                    }
                                                }}
                                                height={300}
                                            />
                                        </div>
                                    )}

                                    {/* Activity Heatmap (Mock) */}
                                    {engagementData.heatmap && (
                                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Student Activity Heatmap (Last 4 Weeks)</h2>
                                            <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']}
                                            </div>
                                            <div className="space-y-1">
                                                {engagementData.heatmap.map((week, weekIndex) => (
                                                    <div key={weekIndex} className="flex space-x-1">
                                                        {week.map((day, dayIndex) => (
                                                            <div
                                                                key={`${weekIndex}-${dayIndex}`}
                                                                title={`${day.date}: ${day.count} interactions`}
                                                                className="w-8 h-8 rounded flex items-center justify-center text-xs font-medium text-neutral-700 dark:text-neutral-300"
                                                                style={{
                                                                    backgroundColor: `rgba(16, 185, 129, ${day.intensity})`
                                                                }}
                                                            >
                                                                {day.count > 0 ? day.count : ''}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-4 text-xs text-neutral-500 dark:text-neutral-400">
                                                <span className="inline-block w-3 h-3 bg-green-200 rounded mr-2"></span>
                                                Low activity →
                                                <span className="inline-block w-3 h-3 bg-green-500 rounded mx-2"></span>
                                                High activity
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* Demographics Tab */}
                    {activeTab === 'demographics' && (
                        <div className="space-y-8">
                            {studentDemographics ? (
                                <>
                                    {/* Age Distribution */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Age Distribution</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(studentDemographics.ageGroups || {}),
                                                datasets: [
                                                    {
                                                        label: 'Students by Age Group',
                                                        data: Object.values(studentDemographics.ageGroups || {}),
                                                        backgroundColor: '#3b82f6'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>

                                    {/* Gender Distribution */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Gender Distribution</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(studentDemographics.genderDistribution),
                                                datasets: [
                                                    {
                                                        label: 'Students by Gender',
                                                        data: Object.values(studentDemographics.genderDistribution),
                                                        backgroundColor: '#10b981'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>

                                    {/* Education Level */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Education Level</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(studentDemographics.educationDistribution),
                                                datasets: [
                                                    {
                                                        label: 'Students by Education Level',
                                                        data: Object.values(studentDemographics.educationDistribution),
                                                        backgroundColor: '#f59e0b'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <div className="text-center py-12">
                                        <div className="text-6xl mb-4">📊</div>
                                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses created yet</h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                            Create your first course to start tracking performance
                                        </p>

                                        <button
                                            onClick={() => navigate('/instructor/course/builder')}
                                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            Create Course
                                        </button>

                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Geography Tab */}
                    {activeTab === 'geography' && (
                        <div className="space-y-8">
                            {geographicDistribution ? (
                                <>
                                    {/* Country Distribution */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Top Countries</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(geographicDistribution.countries || {}).slice(0, 10),
                                                datasets: [
                                                    {
                                                        label: 'Students by Country',
                                                        data: Object.values(geographicDistribution.countries || {}).slice(0, 10),
                                                        backgroundColor: '#8b5cf6'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>

                                    {/* State Distribution */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Top States/Regions</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(geographicDistribution.states || {}).slice(0, 10),
                                                datasets: [
                                                    {
                                                        label: 'Students by State/Region',
                                                        data: Object.values(geographicDistribution.states || {}).slice(0, 10),
                                                        backgroundColor: '#ec4899'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>

                                    {/* City Distribution */}
                                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Top Cities</h2>
                                        <BarChart
                                            data={{
                                                labels: Object.keys(geographicDistribution.cities || {}).slice(0, 10),
                                                datasets: [
                                                    {
                                                        label: 'Students by City',
                                                        data: Object.values(geographicDistribution.cities || {}).slice(0, 10),
                                                        backgroundColor: '#f59e0b'
                                                    }
                                                ]
                                            }}
                                            height={300}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <div className="text-center py-12">
                                        <div className="text-6xl mb-4">📊</div>
                                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses created yet</h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                            Create your first course to start tracking performance
                                        </p>

                                        <button
                                            onClick={() => navigate('/instructor/course/builder')}
                                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            Create Course
                                        </button>

                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Cohorts Tab */}
                    {activeTab === 'cohorts' && (
                        <div className="space-y-8">
                            {cohortAnalysis && cohortAnalysis.length > 0 ? (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Cohort Retention Analysis</h2>
                                    <div className="space-y-6">
                                        {cohortAnalysis.map((cohort, index) => (
                                            <div key={index} className="mb-8">
                                                <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
                                                    Cohort: {cohort.cohort} ({cohort.cohortSize} students)
                                                </h3>
                                                <LineChart
                                                    data={{
                                                        labels: cohort.retentionData.map(data => `Month ${data.month}`),
                                                        datasets: [
                                                            {
                                                                label: 'Retention Rate',
                                                                data: cohort.retentionData.map(data => data.retentionRate),
                                                                borderColor: '#3b82f6',
                                                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                                fill: true
                                                            }
                                                        ]
                                                    }}
                                                    options={{
                                                        scales: {
                                                            y: {
                                                                ticks: {
                                                                    callback: function (value) {
                                                                        return `${value}%`;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    height={300}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                                    <div className="text-center py-12">
                                        <div className="text-6xl mb-4">📊</div>
                                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No courses created yet</h3>
                                        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                            Create your first course to start tracking performance
                                        </p>

                                        <button
                                            onClick={() => navigate('/instructor/course/builder')}
                                            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            Create Course
                                        </button>

                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                </div>
            </div>
        </DashboardLayout>
    );
};

export default InstructorAnalyticsPage;